import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { FileQuestion, Plus, Edit, Trash2, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuestionBankTab({ token, questionBank, fetchQuestionBank }) {
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question: '',
    options: ['', '', '', ''],
    correct_answer: '',
    category: '',
    difficulty: 'medium',
    explanation: ''
  });

  const handleSaveQuestion = async () => {
    if (!questionForm.question || questionForm.options.filter(o => o).length < 2 || !questionForm.correct_answer) {
      toast.error('Please fill in question, at least 2 options, and correct answer');
      return;
    }
    try {
      if (editingQuestion) {
        await axios.put(`${API}/examiner/question-bank/${editingQuestion.id}`, questionForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Question updated successfully');
      } else {
        await axios.post(`${API}/examiner/question-bank`, questionForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Question added successfully');
      }
      setShowQuestionForm(false);
      setEditingQuestion(null);
      setQuestionForm({ question: '', options: ['', '', '', ''], correct_answer: '', category: '', difficulty: 'medium', explanation: '' });
      fetchQuestionBank();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save question'); }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    try {
      await axios.delete(`${API}/examiner/question-bank/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Question deleted');
      fetchQuestionBank();
    } catch (err) { toast.error('Failed to delete question'); }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-[#1B2A4A]">Question Bank</h3>
          <p className="text-sm text-slate-500">Create and manage MCQ questions for examinations</p>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            className="rounded-sm"
            onClick={() => setShowBulkUpload(true)}
          >
            <Upload className="h-4 w-4 mr-1" /> Bulk Upload
          </Button>
          <Button 
            size="sm" 
            onClick={() => {
              setEditingQuestion(null);
              setQuestionForm({ question: '', options: ['', '', '', ''], correct_answer: '', category: '', difficulty: 'medium', explanation: '' });
              setShowQuestionForm(true);
            }} 
            className="bg-[#1B2A4A] text-white rounded-sm"
          >
            <Plus className="h-4 w-4 mr-1" /> Add Question
          </Button>
        </div>
      </div>

      {/* Question Form */}
      {showQuestionForm && (
        <Card className="imcac-card mb-4">
          <CardContent className="p-5">
            <h4 className="text-md font-semibold text-[#1B2A4A] mb-3">
              {editingQuestion ? 'Edit Question' : 'Add New Question'}
            </h4>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Question</Label>
                <Textarea
                  value={questionForm.question}
                  onChange={e => setQuestionForm({...questionForm, question: e.target.value})}
                  placeholder="Enter your question here..."
                  className="rounded-sm mt-1"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {questionForm.options.map((opt, idx) => (
                  <div key={idx}>
                    <Label className="text-xs">Option {String.fromCharCode(65 + idx)}</Label>
                    <Input
                      value={opt}
                      onChange={e => {
                        const newOptions = [...questionForm.options];
                        newOptions[idx] = e.target.value;
                        setQuestionForm({...questionForm, options: newOptions});
                      }}
                      placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                      className="rounded-sm mt-1 text-sm"
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Correct Answer</Label>
                  <Select
                    value={questionForm.correct_answer}
                    onValueChange={v => setQuestionForm({...questionForm, correct_answer: v})}
                  >
                    <SelectTrigger className="rounded-sm mt-1 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {questionForm.options.map((opt, idx) => (
                        opt && <SelectItem key={idx} value={opt}>Option {String.fromCharCode(65 + idx)}: {opt.substring(0, 30)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Category</Label>
                  <Input
                    value={questionForm.category}
                    onChange={e => setQuestionForm({...questionForm, category: e.target.value})}
                    placeholder="e.g., Anatomy, Procedures"
                    className="rounded-sm mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Difficulty</Label>
                  <Select
                    value={questionForm.difficulty}
                    onValueChange={v => setQuestionForm({...questionForm, difficulty: v})}
                  >
                    <SelectTrigger className="rounded-sm mt-1 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Explanation (Optional)</Label>
                <Textarea
                  value={questionForm.explanation}
                  onChange={e => setQuestionForm({...questionForm, explanation: e.target.value})}
                  placeholder="Explain why this is the correct answer..."
                  className="rounded-sm mt-1"
                  rows={2}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSaveQuestion} className="bg-[#1B2A4A] text-white rounded-sm">
                  {editingQuestion ? 'Update Question' : 'Add Question'}
                </Button>
                <Button variant="outline" onClick={() => { setShowQuestionForm(false); setEditingQuestion(null); }} className="rounded-sm">
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowBulkUpload(false)}>
          <Card className="max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-lg">Bulk Upload Questions</CardTitle>
              <CardDescription>Upload multiple questions from a CSV file</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg text-xs">
                <p className="font-semibold mb-2">CSV Format:</p>
                <code className="text-slate-600">question, option_a, option_b, option_c, option_d, correct_answer, category, difficulty</code>
                <p className="mt-2 text-slate-500">• correct_answer should be A, B, C, or D</p>
                <p className="text-slate-500">• difficulty: easy, medium, or hard</p>
              </div>
              
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <Download className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                <p className="text-sm text-slate-600 mb-2">Drag and drop your CSV file here, or click to browse</p>
                <Input
                  type="file"
                  accept=".csv"
                  className="max-w-xs mx-auto"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      try {
                        const text = await file.text();
                        const parseCSVLine = (line) => {
                          const result = [];
                          let current = '';
                          let inQuotes = false;
                          for (let i = 0; i < line.length; i++) {
                            const char = line[i];
                            if (char === '"') {
                              inQuotes = !inQuotes;
                            } else if (char === ',' && !inQuotes) {
                              result.push(current.trim().replace(/^"|"$/g, ''));
                              current = '';
                            } else {
                              current += char;
                            }
                          }
                          result.push(current.trim().replace(/^"|"$/g, ''));
                          return result;
                        };
                        
                        const lines = text.split('\n').filter(l => l.trim());
                        
                        const questions = [];
                        for (let i = 1; i < lines.length; i++) {
                          const values = parseCSVLine(lines[i]);
                          if (values.length >= 8 && values[0]) {
                            const correctAnswerLetter = values[5]?.toUpperCase();
                            const optionIndex = correctAnswerLetter.charCodeAt(0) - 65;
                            const correctAnswer = optionIndex >= 0 && optionIndex <= 3 
                              ? values[optionIndex + 1] 
                              : values[1];
                            
                            questions.push({
                              question: values[0],
                              options: [values[1], values[2], values[3], values[4]],
                              correct_answer: correctAnswer,
                              category: values[6] || 'General',
                              difficulty: values[7] || 'medium'
                            });
                          }
                        }
                        
                        if (questions.length === 0) {
                          toast.error('No valid questions found in CSV file');
                          return;
                        }
                        
                        const response = await axios.post(`${API}/examiner/question-bank/bulk`, 
                          { questions },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        
                        toast.success(`Successfully uploaded ${response.data.count || questions.length} questions`);
                        setShowBulkUpload(false);
                        fetchQuestionBank();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to upload questions');
                      }
                    }
                  }}
                />
              </div>
              
              <a 
                href="/sample_questions.csv" 
                download
                className="text-sm text-[#C5A572] hover:underline"
              >
                Download Sample Template
              </a>
              
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowBulkUpload(false)} className="rounded-sm">
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Questions List */}
      {questionBank.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <FileQuestion className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>No questions in your question bank yet.</p>
          <p className="text-sm">Start by adding questions or uploading a CSV file.</p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead className="text-xs font-bold w-12">#</TableHead>
                <TableHead className="text-xs font-bold">Question</TableHead>
                <TableHead className="text-xs font-bold">Category</TableHead>
                <TableHead className="text-xs font-bold">Difficulty</TableHead>
                <TableHead className="text-xs font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {questionBank.map((q, idx) => (
                <TableRow key={q.id}>
                  <TableCell className="text-sm text-slate-500">{idx + 1}</TableCell>
                  <TableCell>
                    <p className="text-sm text-[#1B2A4A] line-clamp-2">{q.question}</p>
                    <p className="text-xs text-green-600 mt-1">Answer: {q.correct_answer?.substring(0, 50)}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{q.category || 'General'}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-xs ${
                      q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                      q.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {q.difficulty || 'medium'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 w-7 p-0 rounded-sm"
                        onClick={() => {
                          setEditingQuestion(q);
                          setQuestionForm({
                            question: q.question,
                            options: q.options || ['', '', '', ''],
                            correct_answer: q.correct_answer,
                            category: q.category || '',
                            difficulty: q.difficulty || 'medium',
                            explanation: q.explanation || ''
                          });
                          setShowQuestionForm(true);
                        }}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 w-7 p-0 rounded-sm text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteQuestion(q.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
